import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Copy, Link2, Plus, Trash2, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface TrackingLink {
  id: string;
  link_name: string;
  base_url: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  clicks: number;
  conversions: number;
  revenue_generated: number;
  is_active: boolean;
  created_at: string;
}

export const TrackingLinkGenerator = ({ referralCode }: { referralCode: string }) => {
  const [links, setLinks] = useState<TrackingLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    link_name: "",
    base_url: window.location.origin,
    utm_source: "affiliate",
    utm_medium: "referral",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
  });

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("tracking_links")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLinks(data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load tracking links");
    } finally {
      setLoading(false);
    }
  };

  const generateFullUrl = (link: TrackingLink | typeof formData) => {
    const params = new URLSearchParams();
    params.append("ref", referralCode);
    
    if (link.utm_source) params.append("utm_source", link.utm_source);
    if (link.utm_medium) params.append("utm_medium", link.utm_medium);
    if (link.utm_campaign) params.append("utm_campaign", link.utm_campaign);
    if (link.utm_term) params.append("utm_term", link.utm_term);
    if (link.utm_content) params.append("utm_content", link.utm_content);

    return `${link.base_url}/auth?${params.toString()}`;
  };

  const createLink = async () => {
    if (!formData.link_name || !formData.utm_campaign) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("tracking_links").insert({
        user_id: user.id,
        ...formData,
      });

      toast.success("Tracking link created!");
      setIsOpen(false);
      setFormData({
        link_name: "",
        base_url: window.location.origin,
        utm_source: "affiliate",
        utm_medium: "referral",
        utm_campaign: "",
        utm_term: "",
        utm_content: "",
      });
      loadLinks();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create link");
    }
  };

  const copyLink = (link: TrackingLink) => {
    navigator.clipboard.writeText(generateFullUrl(link));
    toast.success("Link copied to clipboard!");
  };

  const deleteLink = async (id: string) => {
    try {
      await supabase
        .from("tracking_links")
        .update({ is_active: false })
        .eq("id", id);

      toast.success("Link deleted");
      loadLinks();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete link");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Custom Tracking Links</CardTitle>
            <CardDescription>
              Create custom links with UTM parameters for advanced tracking
            </CardDescription>
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Create Link
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Custom Tracking Link</DialogTitle>
                <DialogDescription>
                  Generate a custom referral link with UTM parameters
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="link_name">Link Name *</Label>
                    <Input
                      id="link_name"
                      placeholder="Summer Campaign 2024"
                      value={formData.link_name}
                      onChange={(e) =>
                        setFormData({ ...formData, link_name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="utm_campaign">Campaign *</Label>
                    <Input
                      id="utm_campaign"
                      placeholder="summer_sale"
                      value={formData.utm_campaign}
                      onChange={(e) =>
                        setFormData({ ...formData, utm_campaign: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="utm_source">Source</Label>
                    <Input
                      id="utm_source"
                      placeholder="affiliate"
                      value={formData.utm_source}
                      onChange={(e) =>
                        setFormData({ ...formData, utm_source: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="utm_medium">Medium</Label>
                    <Input
                      id="utm_medium"
                      placeholder="referral"
                      value={formData.utm_medium}
                      onChange={(e) =>
                        setFormData({ ...formData, utm_medium: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="utm_term">Term</Label>
                    <Input
                      id="utm_term"
                      placeholder="keyword"
                      value={formData.utm_term}
                      onChange={(e) =>
                        setFormData({ ...formData, utm_term: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="utm_content">Content</Label>
                    <Input
                      id="utm_content"
                      placeholder="banner_ad"
                      value={formData.utm_content}
                      onChange={(e) =>
                        setFormData({ ...formData, utm_content: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Preview</Label>
                  <div className="p-3 bg-muted rounded-lg text-sm break-all">
                    {generateFullUrl(formData)}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createLink}>Create Link</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No tracking links created yet
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Clicks</TableHead>
                <TableHead>Conversions</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((link) => (
                <TableRow key={link.id}>
                  <TableCell className="font-medium">{link.link_name}</TableCell>
                  <TableCell>{link.utm_campaign}</TableCell>
                  <TableCell>{link.clicks}</TableCell>
                  <TableCell>{link.conversions}</TableCell>
                  <TableCell>
                    {link.clicks > 0
                      ? ((link.conversions / link.clicks) * 100).toFixed(1)
                      : 0}%
                  </TableCell>
                  <TableCell>
                    ${(link.revenue_generated / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyLink(link)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteLink(link.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
